import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { protocolsAPI, documentTypesAPI, usersAPI, ocrAPI, assigneesAPI, locationsAPI, contactsAPI, onedriveAPI, emailQueueAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ArrowLeft,
  Save,
  Loader2,
  Upload,
  X,
  FileText,
  ArrowDownLeft,
  ArrowUpRight,
  Link as LinkIcon,
  FolderOpen,
  Eye,
  Image,
  File,
  UserCircle,
  Scan,
  Sparkles,
  MapPin,
  Users,
  PlusCircle,
  Cloud,
  Search,
} from "lucide-react";
import { OneDriveFolderPicker } from "../components/OneDriveFolderPicker";
import { OneDriveFilePicker } from "../components/OneDriveFilePicker";

export function ProtocolFormPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const fromEmailId = searchParams.get("from_email");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [availableProtocols, setAvailableProtocols] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableAssignees, setAvailableAssignees] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [availableContacts, setAvailableContacts] = useState([]);
  const [message, setMessage] = useState(null);
  
  const [formData, setFormData] = useState({
    direction: "entrata",
    document_type: "",
    subject: "",
    sender: "",
    recipient: "",
    notes: "",
    related_protocol_id: "",
    assigned_to: "",
    location_id: "",
    contact_id: "",
    contact_category: "",
    sender_protocol_number: "",
    document_date: "",
    registration_date: new Date().toISOString().split("T")[0],
    protocol_number: "",  // Only used in edit mode
  });
  
  const [attachments, setAttachments] = useState([]);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [newContactData, setNewContactData] = useState({ name: "", category: "clienti" });
  const [savingContact, setSavingContact] = useState(false);
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  
  // Preview protocol number state
  const [previewProtocolNumber, setPreviewProtocolNumber] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const ocrFileInputRef = useRef(null);
  const [onedriveFolder, setOnedriveFolder] = useState("");
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [onedriveConnected, setOnedriveConnected] = useState(false);
  const [showFilePicker, setShowFilePicker] = useState(false);
  
  // Related protocol search states
  const [protocolSearchQuery, setProtocolSearchQuery] = useState("");
  const [protocolSearchResults, setProtocolSearchResults] = useState([]);
  const [protocolSearchLoading, setProtocolSearchLoading] = useState(false);
  const [showProtocolResults, setShowProtocolResults] = useState(false);
  const [selectedRelatedProtocol, setSelectedRelatedProtocol] = useState(null);
  const protocolSearchRef = useRef(null);
  const [emailSource, setEmailSource] = useState(null);

  useEffect(() => {
    loadDocumentTypes();
    loadAvailableProtocols();
    loadAvailableUsers();
    loadAvailableAssignees();
    loadAvailableLocations();
    loadAvailableContacts();
    loadOnedriveStatus();
    if (isEdit) {
      loadProtocol();
    }
    if (fromEmailId && !isEdit) {
      loadEmailData(fromEmailId);
    }
    // Check for duplicate data
    const duplicateParam = searchParams.get("duplicate");
    if (duplicateParam === "true" && !isEdit) {
      const savedData = sessionStorage.getItem("duplicateProtocol");
      if (savedData) {
        try {
          const duplicateData = JSON.parse(savedData);
          setFormData((prev) => ({
            ...prev,
            ...duplicateData,
          }));
          sessionStorage.removeItem("duplicateProtocol");
          showMessage("Dati del protocollo copiati. Modifica e salva come nuovo.", "success");
        } catch {
          // Invalid JSON, ignore
        }
      }
    }
  }, [id]);

  // Load preview protocol number when direction or registration_date changes (only for new protocols)
  useEffect(() => {
    if (!isEdit) {
      loadPreviewNumber();
    }
  }, [formData.direction, formData.registration_date, isEdit]);

  const loadPreviewNumber = async () => {
    setLoadingPreview(true);
    try {
      const params = {
        direction: formData.direction,
      };
      if (formData.registration_date) {
        params.registration_date = formData.registration_date;
      }
      const response = await protocolsAPI.previewNumber(params);
      setPreviewProtocolNumber(response.data.protocol_number);
    } catch (error) {
      console.error("Error loading preview number:", error);
      setPreviewProtocolNumber("");
    } finally {
      setLoadingPreview(false);
    }
  };

  const loadOnedriveStatus = async () => {
    try {
      const res = await onedriveAPI.getStatus();
      setOnedriveConnected(res.data.connected);
    } catch { /* ignore */ }
  };

  const loadEmailData = async (emailId) => {
    try {
      const res = await emailQueueAPI.getFull(emailId);
      const email = res.data;
      setEmailSource(email);
      setFormData((prev) => ({
        ...prev,
        direction: "entrata",
        document_type: email.document_type || prev.document_type,
        subject: email.subject || "",
        sender: email.sender || "",
        recipient: email.recipients?.join(", ") || "",
        notes: `Importato da email - ${email.account_name}\n\n${email.body_preview || ""}`,
      }));
      // Pre-load email attachments
      if (email.attachments && email.attachments.length > 0) {
        setAttachments(email.attachments.map((att) => ({
          filename: att.filename,
          data: att.data,
          size: att.size,
          mime_type: att.mime_type,
          file_path: "",
        })));
      }
    } catch {
      showMessage("Errore nel caricamento dei dati email", "error");
    }
  };

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    // Longer timeout for errors so users can read them
    const timeout = type === "error" ? 8000 : 3000;
    setTimeout(() => setMessage(null), timeout);
  };

  const loadDocumentTypes = async () => {
    try {
      const response = await documentTypesAPI.getAll();
      setDocumentTypes(response.data);
      if (!isEdit && response.data.length > 0) {
        setFormData((prev) => ({ ...prev, document_type: response.data[0].name }));
      }
    } catch (error) {
      showMessage("Errore nel caricamento delle tipologie", "error");
    }
  };

  const loadAvailableProtocols = async () => {
    try {
      const response = await protocolsAPI.getAll({ page_size: 100 });
      setAvailableProtocols(response.data.items);
    } catch (error) {
      console.error("Error loading protocols:", error);
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setAvailableUsers(response.data.filter(u => u.is_active));
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  const loadAvailableAssignees = async () => {
    try {
      const response = await assigneesAPI.getAll();
      setAvailableAssignees(response.data);
    } catch (error) {
      console.error("Error loading assignees:", error);
    }
  };

  const loadAvailableLocations = async () => {
    try {
      const response = await locationsAPI.getAll();
      setAvailableLocations(response.data);
    } catch (error) {
      console.error("Error loading locations:", error);
    }
  };

  const loadAvailableContacts = async () => {
    try {
      const response = await contactsAPI.getAll({});
      setAvailableContacts(response.data);
    } catch (error) {
      console.error("Error loading contacts:", error);
    }
  };

  const handleCreateContact = async () => {
    if (!newContactData.name.trim()) {
      showMessage("Inserisci il nome del contatto", "error");
      return;
    }
    setSavingContact(true);
    try {
      const response = await contactsAPI.create(newContactData);
      const newContact = response.data;
      // Add to available contacts and select it
      setAvailableContacts(prev => [...prev, newContact]);
      setFormData(prev => ({ ...prev, contact_id: newContact.id }));
      setShowNewContactModal(false);
      setNewContactData({ name: "", category: "clienti" });
      showMessage("Contatto creato e selezionato");
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore nella creazione del contatto", "error");
    } finally {
      setSavingContact(false);
    }
  };

  const loadProtocol = async () => {
    setLoading(true);
    try {
      const response = await protocolsAPI.getOne(id);
      const protocol = response.data;
      setFormData({
        direction: protocol.direction,
        document_type: protocol.document_type,
        subject: protocol.subject,
        sender: protocol.sender || "",
        recipient: protocol.recipient || "",
        notes: protocol.notes || "",
        related_protocol_id: protocol.related_protocol_id || "",
        assigned_to: protocol.assigned_to || "",
        location_id: protocol.location_id || "",
        contact_id: protocol.contact_id || "",
        contact_category: protocol.contact_category || "",
        sender_protocol_number: protocol.sender_protocol_number || "",
        document_date: protocol.document_date || "",
        registration_date: protocol.registration_date || "",
        protocol_number: protocol.protocol_number || "",
      });
      setAttachments(protocol.attachments || []);
      setOnedriveFolder(protocol.onedrive_folder || "");
      
      // Load related protocol info if exists
      if (protocol.related_protocol_id) {
        try {
          const relatedResponse = await protocolsAPI.getOne(protocol.related_protocol_id);
          setSelectedRelatedProtocol(relatedResponse.data);
        } catch {
          // Related protocol might have been deleted
        }
      }
    } catch (error) {
      showMessage("Errore nel caricamento del protocollo", "error");
      navigate("/protocols");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        showMessage("Il file " + file.name + " supera i 10MB", "error");
        continue;
      }
      
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        const mimeType = file.type;
        setAttachments((prev) => [
          ...prev,
          { 
            filename: file.name, 
            data: base64, 
            size: file.size,
            mime_type: mimeType,
            file_path: "" 
          },
        ]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFilePathChange = (index, path) => {
    setAttachments((prev) => 
      prev.map((att, i) => i === index ? { ...att, file_path: path } : att)
    );
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    if (previewAttachment === index) {
      setPreviewAttachment(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const getFileIcon = (filename, mimeType) => {
    if (mimeType?.startsWith("image/")) return Image;
    if (mimeType === "application/pdf") return FileText;
    return File;
  };

  const canPreview = (attachment) => {
    const mime = attachment.mime_type || "";
    return mime.startsWith("image/") || mime === "application/pdf";
  };

  // OCR Functions
  const handleOCRFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const ext = file.name.split('.').pop().toLowerCase();
    const supportedFormats = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'pdf'];
    
    if (!supportedFormats.includes(ext)) {
      showMessage("Formato non supportato per OCR. Usa immagini o PDF.", "error");
      return;
    }
    
    setOcrProcessing(true);
    setOcrResult(null);
    
    try {
      const response = await ocrAPI.extract(file);
      const data = response.data;
      
      if (data.success) {
        setOcrResult(data);
        showMessage("Testo estratto con successo!");
      } else {
        showMessage(data.text || "Errore nell'estrazione del testo", "error");
      }
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore durante l'OCR", "error");
    } finally {
      setOcrProcessing(false);
      if (ocrFileInputRef.current) {
        ocrFileInputRef.current.value = "";
      }
    }
  };

  const applyOCRSuggestion = (field, value) => {
    if (value) {
      setFormData((prev) => ({ ...prev, [field]: value }));
      showMessage(`Campo "${field}" compilato automaticamente`);
    }
  };

  const applyAllOCRSuggestions = () => {
    if (!ocrResult) return;
    
    const updates = {};
    if (ocrResult.suggested_subject && !formData.subject) {
      updates.subject = ocrResult.suggested_subject;
    }
    if (ocrResult.suggested_sender && !formData.sender) {
      updates.sender = ocrResult.suggested_sender;
    }
    
    if (Object.keys(updates).length > 0) {
      setFormData((prev) => ({ ...prev, ...updates }));
      showMessage("Campi compilati automaticamente");
    } else {
      showMessage("Nessun campo da compilare", "error");
    }
  };

  const renderPreview = (attachment) => {
    if (!attachment.data) return null;
    
    const mime = attachment.mime_type || "";
    const dataUrl = "data:" + mime + ";base64," + attachment.data;
    
    if (mime.startsWith("image/")) {
      return (
        <img 
          src={dataUrl} 
          alt={attachment.filename}
          className="attachment-preview max-w-full max-h-[300px] object-contain rounded-lg border border-border"
        />
      );
    }
    
    if (mime === "application/pdf") {
      return (
        <iframe 
          src={dataUrl}
          title={attachment.filename}
          className="w-full h-[400px] rounded-lg border border-border"
        />
      );
    }
    
    return <p className="text-muted-foreground">Anteprima non disponibile</p>;
  };

  // Search related protocols with debounce
  const searchRelatedProtocols = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setProtocolSearchResults([]);
      setShowProtocolResults(false);
      return;
    }
    
    setProtocolSearchLoading(true);
    try {
      const response = await protocolsAPI.getAll({ 
        search: query, 
        page_size: 15,
        sort_by: "number",
        sort_order: "desc"
      });
      const results = response.data.items.filter(p => p.id !== id); // Exclude current protocol
      setProtocolSearchResults(results);
      setShowProtocolResults(true);
    } catch (error) {
      console.error("Error searching protocols:", error);
    } finally {
      setProtocolSearchLoading(false);
    }
  }, [id]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (protocolSearchQuery) {
        searchRelatedProtocols(protocolSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [protocolSearchQuery, searchRelatedProtocols]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (protocolSearchRef.current && !protocolSearchRef.current.contains(event.target)) {
        setShowProtocolResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Select a related protocol
  const selectRelatedProtocol = (protocol) => {
    setSelectedRelatedProtocol(protocol);
    handleChange("related_protocol_id", protocol.id);
    setProtocolSearchQuery("");
    setShowProtocolResults(false);
  };

  // Clear selected related protocol
  const clearRelatedProtocol = () => {
    setSelectedRelatedProtocol(null);
    handleChange("related_protocol_id", "");
    setProtocolSearchQuery("");
  };

  // Format date for display
  const formatSearchDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return dateStr.substring(0, 10);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    
    if (!formData.document_type) {
      showMessage("Seleziona una tipologia", "error");
      return;
    }
    if (!formData.subject.trim()) {
      showMessage("Inserisci l'oggetto del protocollo", "error");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        attachments: attachments,
        related_protocol_id: formData.related_protocol_id || null,
        assigned_to: formData.assigned_to || null,
        onedrive_folder: onedriveFolder || null,
        document_date: formData.document_date || null,
        registration_date: formData.registration_date || null,
        contact_id: formData.contact_id || null,
        contact_category: formData.contact_category || null,
        sender_protocol_number: formData.sender_protocol_number || null,
      };

      if (isEdit) {
        await protocolsAPI.update(id, payload);
        navigate("/protocols?updated=true");
      } else {
        const response = await protocolsAPI.create(payload);
        // If from email, mark the email as processed (without creating another protocol)
        if (fromEmailId) {
          try { 
            await emailQueueAPI.markProcessed(fromEmailId, response.data.protocol_number); 
          } catch { /* ignore */ }
        }
        navigate("/protocols?saved=true");
      }
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore durante il salvataggio", "error");
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D7BB7]" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in">
      {/* Message Banner */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          message.type === "error" 
            ? "bg-red-500 text-white" 
            : "bg-emerald-500 text-white"
        }`}>
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleCancel}
          className="text-muted-foreground hover:text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft size={24} />
        </Button>
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">
            {isEdit ? "Modifica Protocollo" : "Nuovo Protocollo"}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isEdit ? "Modifica i dettagli del protocollo" : "Registra un nuovo protocollo in entrata o uscita"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading text-foreground">
              Informazioni Protocollo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Direction */}
            <div className="space-y-2">
              <Label className="text-foreground">Direzione *</Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleChange("direction", "entrata")}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
                    formData.direction === "entrata"
                      ? "bg-emerald-500 text-white border-emerald-500"
                      : "bg-background text-muted-foreground border-border hover:bg-muted"
                  }`}
                  data-testid="direction-entrata"
                >
                  <ArrowDownLeft size={18} />
                  Entrata
                </button>
                <button
                  type="button"
                  onClick={() => handleChange("direction", "uscita")}
                  className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border transition-colors ${
                    formData.direction === "uscita"
                      ? "bg-amber-500 text-white border-amber-500"
                      : "bg-background text-muted-foreground border-border hover:bg-muted"
                  }`}
                  data-testid="direction-uscita"
                >
                  <ArrowUpRight size={18} />
                  Uscita
                </button>
              </div>
              {isEdit && (
                <p className="text-xs text-amber-600">
                  Attenzione: modificando la direzione, il numero protocollo verra aggiornato automaticamente (E ↔ U)
                </p>
              )}
            </div>

            {/* Preview Protocol Number - Only for new protocols */}
            {!isEdit && (
              <div className="p-4 bg-gradient-to-r from-sky-50 to-blue-50 dark:from-sky-950/30 dark:to-blue-950/30 rounded-lg border border-sky-200 dark:border-sky-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Numero Protocollo che verrà assegnato:</p>
                    <div className="flex items-center gap-2">
                      {loadingPreview ? (
                        <Loader2 size={18} className="animate-spin text-[#2D7BB7]" />
                      ) : (
                        <span className="text-2xl font-mono font-bold text-[#2D7BB7]" data-testid="preview-protocol-number">
                          {previewProtocolNumber || "---"}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    formData.direction === "entrata" 
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300" 
                      : "bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
                  }`}>
                    {formData.direction === "entrata" ? "Entrata" : "Uscita"}
                  </div>
                </div>
                {previewProtocolNumber?.includes("/A") && (
                  <p className="text-xs text-amber-600 mt-2">
                    ⚠️ Protocollo antedatato - Il numero include il suffisso /A
                  </p>
                )}
              </div>
            )}

            {/* Protocol Number - Only in edit mode */}
            {isEdit && (
              <div className="space-y-2">
                <Label className="text-foreground">Numero Protocollo</Label>
                <Input
                  type="text"
                  value={formData.protocol_number}
                  onChange={(e) => handleChange("protocol_number", e.target.value)}
                  placeholder="Es: 26/E/0001"
                  className="bg-background border-input text-foreground font-mono"
                  data-testid="protocol-number-input"
                />
                <p className="text-xs text-amber-600">
                  Attenzione: modifica il numero protocollo solo se strettamente necessario. Formato: AA/D/NNNN (es: 26/E/0001)
                </p>
              </div>
            )}

            {/* Dates: Document Date and Registration Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-foreground">Data Documento</Label>
                <Input
                  type="date"
                  value={formData.document_date}
                  onChange={(e) => handleChange("document_date", e.target.value)}
                  className="bg-background border-input text-foreground"
                  data-testid="document-date-input"
                />
                <p className="text-xs text-muted-foreground">
                  Data riportata sul documento
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-foreground">Data Registrazione</Label>
                <Input
                  type="date"
                  value={formData.registration_date}
                  onChange={(e) => handleChange("registration_date", e.target.value)}
                  className="bg-background border-input text-foreground"
                  data-testid="registration-date-input"
                />
                <p className="text-xs text-muted-foreground">
                  {isEdit 
                    ? "Data in cui e stato registrato il protocollo"
                    : formData.registration_date && formData.registration_date < new Date().toISOString().split("T")[0]
                      ? "Registrazione ante-datata: il protocollo avra suffisso \"A\""
                      : "Data in cui viene registrato il protocollo"
                  }
                </p>
              </div>
            </div>

            {/* Document Type */}
            <div className="space-y-2">
              <Label className="text-foreground">Tipologia Documento *</Label>
              <Select
                value={formData.document_type || undefined}
                onValueChange={(val) => handleChange("document_type", val)}
              >
                <SelectTrigger className="bg-background border-input" data-testid="document-type-select">
                  <SelectValue placeholder="Seleziona tipologia" />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map((type) => (
                    <SelectItem key={type.id} value={type.name}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Related Protocol - Autocomplete Search */}
            <div className="space-y-2">
              <Label className="text-foreground flex items-center gap-2">
                <LinkIcon size={16} />
                Collegato a Protocollo
              </Label>
              
              {/* Show selected protocol or search input */}
              {selectedRelatedProtocol ? (
                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg border border-input">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-[#2D7BB7]">
                        {selectedRelatedProtocol.protocol_number}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        selectedRelatedProtocol.direction === "entrata" 
                          ? "bg-emerald-100 text-emerald-700" 
                          : "bg-amber-100 text-amber-700"
                      }`}>
                        {selectedRelatedProtocol.direction === "entrata" ? "Entrata" : "Uscita"}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatSearchDate(selectedRelatedProtocol.registration_date || selectedRelatedProtocol.created_at)}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground truncate mt-1">
                      {selectedRelatedProtocol.sender || selectedRelatedProtocol.recipient || "-"} • {selectedRelatedProtocol.subject}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearRelatedProtocol}
                    className="shrink-0 h-8 w-8 p-0"
                  >
                    <X size={16} />
                  </Button>
                </div>
              ) : (
                <div className="relative" ref={protocolSearchRef}>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                    <Input
                      type="text"
                      value={protocolSearchQuery}
                      onChange={(e) => setProtocolSearchQuery(e.target.value)}
                      onFocus={() => protocolSearchQuery.length >= 2 && setShowProtocolResults(true)}
                      placeholder="Cerca per numero, oggetto, mittente..."
                      className="pl-9 bg-background border-input text-foreground"
                      data-testid="related-protocol-search"
                    />
                    {protocolSearchLoading && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" size={16} />
                    )}
                  </div>
                  
                  {/* Search Results Dropdown */}
                  {showProtocolResults && protocolSearchResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-[300px] overflow-y-auto">
                      {protocolSearchResults.map((protocol) => (
                        <button
                          key={protocol.id}
                          type="button"
                          onClick={() => selectRelatedProtocol(protocol)}
                          className="w-full text-left px-3 py-2 hover:bg-muted/50 border-b border-border last:border-b-0 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-[#2D7BB7] text-sm">
                              {protocol.protocol_number}
                            </span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              protocol.direction === "entrata" 
                                ? "bg-emerald-100 text-emerald-700" 
                                : "bg-amber-100 text-amber-700"
                            }`}>
                              {protocol.direction === "entrata" ? "E" : "U"}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formatSearchDate(protocol.registration_date || protocol.created_at)}
                            </span>
                          </div>
                          <div className="text-sm text-foreground truncate">
                            <span className="font-medium">{protocol.sender || protocol.recipient || "-"}</span>
                            <span className="text-muted-foreground"> • </span>
                            <span className="text-muted-foreground">{protocol.subject}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* No results message */}
                  {showProtocolResults && protocolSearchQuery.length >= 2 && protocolSearchResults.length === 0 && !protocolSearchLoading && (
                    <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg p-3 text-center text-muted-foreground text-sm">
                      Nessun protocollo trovato
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Digita almeno 2 caratteri per cercare
              </p>
            </div>

            {/* Mittente/Destinatario manuale + Anagrafica + Assegnato a */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Mittente (entrata) o Destinatario (uscita) - input manuale */}
              <div className="space-y-2">
                <Label className="text-foreground flex items-center gap-2">
                  <Users size={16} />
                  {formData.direction === "entrata" ? "Mittente" : "Destinatario"}
                </Label>
                <Input
                  value={formData.direction === "entrata" ? formData.sender : formData.recipient}
                  onChange={(e) => {
                    if (formData.direction === "entrata") {
                      handleChange("sender", e.target.value);
                    } else {
                      handleChange("recipient", e.target.value);
                    }
                  }}
                  placeholder="Inserisci nome manualmente"
                  className="bg-background border-input text-foreground placeholder:text-muted-foreground"
                  data-testid="sender-recipient-input"
                />
                {/* Category selector for manual entry */}
                {!formData.contact_id && (formData.sender || formData.recipient) && (
                  <Select
                    value={formData.contact_category || undefined}
                    onValueChange={(val) => handleChange("contact_category", val)}
                  >
                    <SelectTrigger className="bg-background border-input text-sm" data-testid="contact-category-select">
                      <SelectValue placeholder="Seleziona categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="clienti">Clienti</SelectItem>
                      <SelectItem value="fornitori">Fornitori</SelectItem>
                      <SelectItem value="dipendenti">Dipendenti</SelectItem>
                      <SelectItem value="altri_enti">Altri Enti</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Select
                      value={formData.contact_id || undefined}
                      onValueChange={(val) => {
                        const contactId = val === "__none__" ? "" : val;
                        handleChange("contact_id", contactId);
                        const selectedContact = availableContacts.find(c => c.id === contactId);
                        if (selectedContact) {
                          if (formData.direction === "entrata") {
                            handleChange("sender", selectedContact.name);
                          } else {
                            handleChange("recipient", selectedContact.name);
                          }
                        }
                      }}
                    >
                      <SelectTrigger className="bg-background border-input text-sm" data-testid="contact-select">
                        <SelectValue placeholder="Oppure seleziona dall'anagrafica" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Nessuna selezione</SelectItem>
                        {availableContacts.map((contact) => (
                          <SelectItem key={contact.id} value={contact.id}>
                            {contact.name} ({contact.category})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setShowNewContactModal(true)}
                    className="border-[#2D7BB7] text-[#2D7BB7] hover:bg-blue-50 flex-shrink-0"
                    title="Crea nuovo contatto"
                    data-testid="add-contact-inline-btn"
                  >
                    <PlusCircle size={18} />
                  </Button>
                </div>
              </div>

              {/* Assegnato a (interno) */}
              <div className="space-y-2">
                <Label className="text-foreground flex items-center gap-2">
                  <UserCircle size={16} />
                  {formData.direction === "entrata" ? "Assegnato a" : "Responsabile"}
                </Label>
                <Select
                  value={formData.assigned_to || undefined}
                  onValueChange={(val) => handleChange("assigned_to", val === "__none__" ? "" : val)}
                >
                  <SelectTrigger className="bg-background border-input" data-testid="assigned-to-select">
                    <SelectValue placeholder="Seleziona utente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nessuna assegnazione</SelectItem>
                    {availableAssignees.map((assignee) => (
                      <SelectItem key={assignee.id} value={assignee.id}>
                        {assignee.name} {assignee.role ? `(${assignee.role})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  L'utente ricevera una notifica email
                </p>
              </div>
            </div>

            {/* Sender Protocol Number - only for entrata */}
            {formData.direction === "entrata" && (
              <div className="space-y-2">
                <Label className="text-foreground">N. Protocollo Mittente</Label>
                <Input
                  value={formData.sender_protocol_number}
                  onChange={(e) => handleChange("sender_protocol_number", e.target.value)}
                  placeholder="Eventuale numero di protocollo del documento ricevuto"
                  className="bg-background border-input text-foreground placeholder:text-muted-foreground"
                  data-testid="sender-protocol-number-input"
                />
              </div>
            )}

            {/* Subject */}
            <div className="space-y-2">
              <Label className="text-foreground">Oggetto *</Label>
              <Input
                value={formData.subject}
                onChange={(e) => handleChange("subject", e.target.value)}
                placeholder="Descrizione del documento"
                className="bg-background border-input text-foreground placeholder:text-muted-foreground"
                data-testid="subject-input"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-foreground">Note</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                placeholder="Note aggiuntive (opzionale)"
                rows={4}
                className="bg-background border-input text-foreground placeholder:text-muted-foreground resize-none"
                data-testid="notes-input"
              />
            </div>

            {/* Ubicazione Documento */}
            <div className="space-y-2">
              <Label className="text-foreground flex items-center gap-2">
                <MapPin size={16} />
                Ubicazione Documento
              </Label>
              <Select
                value={formData.location_id || undefined}
                onValueChange={(val) => handleChange("location_id", val === "__none__" ? "" : val)}
              >
                <SelectTrigger className="bg-background border-input" data-testid="location-select">
                  <SelectValue placeholder="Nessuna ubicazione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nessuna ubicazione</SelectItem>
                  {availableLocations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* OCR Scanner */}
            {!isEdit && (
              <div className="space-y-4">
                <Label className="text-foreground flex items-center gap-2">
                  <Scan size={16} className="text-[#2D7BB7]" />
                  Scansione OCR (opzionale)
                </Label>
                <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800 mb-3">
                    Carica un'immagine o PDF scansionato per estrarre automaticamente il testo e compilare i campi.
                  </p>
                  <div className="flex items-center gap-3">
                    <input
                      ref={ocrFileInputRef}
                      type="file"
                      onChange={handleOCRFileSelect}
                      accept=".png,.jpg,.jpeg,.gif,.bmp,.tiff,.pdf"
                      className="hidden"
                      data-testid="ocr-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => ocrFileInputRef.current?.click()}
                      disabled={ocrProcessing}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      data-testid="ocr-scan-btn"
                    >
                      {ocrProcessing ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Scan className="mr-2" size={16} />
                      )}
                      {ocrProcessing ? "Analisi in corso..." : "Scansiona documento"}
                    </Button>
                  </div>
                  
                  {ocrResult && ocrResult.success && (
                    <div className="mt-4 p-3 bg-white rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-blue-800 flex items-center gap-2">
                          <Sparkles size={14} />
                          Suggerimenti estratti
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          onClick={applyAllOCRSuggestions}
                          className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                        >
                          Applica tutti
                        </Button>
                      </div>
                      
                      <div className="space-y-2">
                        {ocrResult.suggested_subject && (
                          <div className="flex items-center justify-between p-2 bg-blue-50 rounded text-sm">
                            <div className="flex-1 truncate">
                              <span className="text-blue-600 font-medium">Oggetto: </span>
                              <span className="text-blue-800">{ocrResult.suggested_subject}</span>
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => applyOCRSuggestion("subject", ocrResult.suggested_subject)}
                              className="text-blue-600 hover:text-blue-800 text-xs ml-2"
                            >
                              Usa
                            </Button>
                          </div>
                        )}
                        {ocrResult.suggested_sender && (
                          <div className="flex items-center justify-between p-2 bg-blue-50 rounded text-sm">
                            <div className="flex-1 truncate">
                              <span className="text-blue-600 font-medium">Mittente: </span>
                              <span className="text-blue-800">{ocrResult.suggested_sender}</span>
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => applyOCRSuggestion("sender", ocrResult.suggested_sender)}
                              className="text-blue-600 hover:text-blue-800 text-xs ml-2"
                            >
                              Usa
                            </Button>
                          </div>
                        )}
                        {ocrResult.suggested_date && (
                          <div className="p-2 bg-blue-50 rounded text-sm">
                            <span className="text-blue-600 font-medium">Data trovata: </span>
                            <span className="text-blue-800">{ocrResult.suggested_date}</span>
                          </div>
                        )}
                      </div>
                      
                      {ocrResult.text && (
                        <details className="mt-3">
                          <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
                            Mostra testo completo estratto
                          </summary>
                          <pre className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-700 max-h-[200px] overflow-auto whitespace-pre-wrap">
                            {ocrResult.text}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Attachments */}
            <div className="space-y-4">
              <Label className="text-foreground">
                {formData.direction === "uscita" ? "Riferimento File" : "Allegati"}
              </Label>

              {formData.direction === "uscita" || (formData.direction === "entrata" && !fromEmailId) ? (
                /* USCITA e ENTRATA (non da email): OneDrive file browser (file già su OneDrive) */
                <>
                  {onedriveConnected ? (
                    <div className="space-y-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowFilePicker(true)}
                        className="w-full border-dashed border-2 border-sky-300 text-[#2D7BB7] hover:bg-sky-50 py-6"
                        data-testid="onedrive-select-file-btn"
                      >
                        <Cloud size={24} className="mr-3" />
                        Seleziona file da OneDrive
                      </Button>
                    </div>
                  ) : (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      Connetti OneDrive nelle impostazioni per selezionare i file.
                    </div>
                  )}
                </>
              ) : (
                /* ENTRATA da email: upload file dall'email */
                <>
                  <div
                    className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-[#2D7BB7] transition-colors cursor-pointer bg-muted/30"
                    onClick={() => document.getElementById("file-input").click()}
                  >
                    <Upload size={40} className="mx-auto text-muted-foreground mb-4" />
                    <p className="text-foreground">
                      Clicca per caricare o trascina i file qui
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      PDF, immagini, documenti (max 10MB per file)
                    </p>
                  </div>
                  <input
                    id="file-input"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                    data-testid="file-input"
                  />
                </>
              )}

              {/* OneDrive Folder Selector (for entrata from email - to save attachments) */}
              {onedriveConnected && formData.direction === "entrata" && fromEmailId && (
                <div className="flex items-center gap-3 p-3 bg-sky-50 border border-sky-200 rounded-lg">
                  <Cloud size={20} className="text-[#2D7BB7] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">Cartella OneDrive per salvare allegati</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {onedriveFolder || "Default: Protocolli/{anno}/{numero}"}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFolderPicker(true)}
                    className="shrink-0 border-sky-300 text-[#2D7BB7] hover:bg-sky-100"
                    data-testid="onedrive-folder-picker-btn"
                  >
                    <FolderOpen size={16} className="mr-1" />
                    {onedriveFolder ? "Cambia" : "Scegli"}
                  </Button>
                  {onedriveFolder && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setOnedriveFolder("")}
                      className="shrink-0 text-muted-foreground hover:text-red-500"
                      data-testid="onedrive-folder-clear-btn"
                    >
                      <X size={16} />
                    </Button>
                  )}
                </div>
              )}

              <OneDriveFolderPicker
                open={showFolderPicker}
                onClose={() => setShowFolderPicker(false)}
                onSelect={(path) => setOnedriveFolder(path)}
              />

              <OneDriveFilePicker
                open={showFilePicker}
                onClose={() => setShowFilePicker(false)}
                onSelect={(files) => setAttachments((prev) => [...prev, ...files])}
              />

              {attachments.length > 0 && (
                <div className="space-y-4">
                  {attachments.map((file, index) => {
                    const FileIcon = getFileIcon(file.filename, file.mime_type);
                    return (
                      <div
                        key={index}
                        className="p-4 bg-muted/50 rounded-lg border border-border"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <FileIcon size={24} className="text-[#2D7BB7]" />
                            <div>
                              <p className="text-sm text-foreground font-medium">{file.filename}</p>
                              {file.size && (
                                <p className="text-xs text-muted-foreground">
                                  {formatFileSize(file.size)}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {canPreview(file) && (
                              <button
                                type="button"
                                onClick={() => setPreviewAttachment(previewAttachment === index ? null : index)}
                                className="flex items-center gap-1 px-3 py-1 text-sm text-[#2D7BB7] hover:text-[#5BA4D4] rounded"
                                data-testid={"preview-attachment-" + index}
                              >
                                <Eye size={16} />
                                {previewAttachment === index ? "Nascondi" : "Anteprima"}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeAttachment(index)}
                              className="p-1 text-muted-foreground hover:text-red-600 rounded"
                              data-testid={"remove-attachment-" + index}
                            >
                              <X size={18} />
                            </button>
                          </div>
                        </div>
                        
                        {/* Preview */}
                        {previewAttachment === index && (
                          <div className="mt-3 p-3 bg-background rounded-lg border border-border">
                            {renderPreview(file)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            className="border-border text-foreground"
            data-testid="cancel-btn"
          >
            Annulla
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white shadow-md"
            data-testid="submit-btn"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvataggio...
              </>
            ) : (
              <>
                <Save className="mr-2" size={18} />
                {isEdit ? "Salva Modifiche" : "Registra Protocollo"}
              </>
            )}
          </Button>
        </div>
      </form>

      {/* New Contact Dialog */}
      <Dialog open={showNewContactModal} onOpenChange={setShowNewContactModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle size={20} className="text-[#2D7BB7]" />
              Nuovo Contatto
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground">Nome *</Label>
              <Input
                value={newContactData.name}
                onChange={(e) => setNewContactData({ ...newContactData, name: e.target.value })}
                placeholder="Nome del contatto"
                className="bg-background border-input text-foreground"
                data-testid="new-contact-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-foreground">Categoria</Label>
              <Select
                value={newContactData.category}
                onValueChange={(val) => setNewContactData({ ...newContactData, category: val })}
              >
                <SelectTrigger className="bg-background border-input" data-testid="new-contact-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="clienti">Clienti</SelectItem>
                  <SelectItem value="fornitori">Fornitori</SelectItem>
                  <SelectItem value="dipendenti">Dipendenti</SelectItem>
                  <SelectItem value="altri_enti">Altri Enti</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowNewContactModal(false);
                setNewContactData({ name: "", category: "clienti" });
              }}
              className="border-border text-foreground"
            >
              Annulla
            </Button>
            <Button
              type="button"
              onClick={handleCreateContact}
              disabled={savingContact}
              className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
              data-testid="save-new-contact-btn"
            >
              {savingContact ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Crea e Seleziona"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
